import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faPlus,
    faEdit,
    faChartBar,
    faUsers,
    faFilter,
    faPaperPlane,
    faCheckCircle,
    faInfoCircle,
    faArrowRight,
    faArrowLeft,
    faEye,
    faCode
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function MarketingEmail() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                        <FontAwesomeIcon icon={faEnvelope} className="text-purple-600 text-2xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Email Marketing
                    </h1>
                    <p className="text-xl text-gray-600">
                        Aprende a crear y enviar campañas de email marketing personalizadas a tus clientes 
                        con segmentación avanzada y análisis de resultados.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-white rounded-lg shadow-md p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                        ¿Qué es Email Marketing?
                    </h2>
                    <p className="text-gray-600 mb-4">
                        El módulo de Email Marketing te permite crear y enviar campañas de email personalizadas 
                        a tus clientes. Con esta herramienta puedes:
                    </p>
                    <ul className="space-y-2 text-gray-600">
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Crear emails con contenido HTML rico usando un editor visual</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Segmentar destinatarios usando análisis RFM avanzado</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Enviar emails masivos con control automático de velocidad</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Ver estadísticas detalladas de cada campaña</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                            <span>Gestionar contactos y su consentimiento (GDPR)</span>
                        </li>
                    </ul>
                </div>

                {/* Acceso al Módulo */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faEnvelope} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Acceder al Módulo
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Cómo Acceder
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>En la barra de navegación superior, haz clic en <strong>"Marketing con IA"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Selecciona <strong>"Email Marketing"</strong> del menú desplegable</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Serás redirigido al Dashboard de Email Marketing</span>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* Dashboard */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faChartBar} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Dashboard de Email Marketing
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Métricas Principales
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El dashboard muestra un resumen rápido de tus campañas y métricas clave:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Total Campañas</h4>
                                <p className="text-sm text-gray-600">
                                    Número total de campañas creadas
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Enviados Hoy</h4>
                                <p className="text-sm text-gray-600">
                                    Emails enviados en el día actual
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Tasa de Éxito</h4>
                                <p className="text-sm text-gray-600">
                                    Porcentaje de emails enviados exitosamente
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Contactos Activos</h4>
                                <p className="text-sm text-gray-600">
                                    Número de contactos con consentimiento activo
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Acciones Rápidas
                        </h3>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faPlus} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Nueva Campaña:</strong> Crear una nueva campaña de email</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faUsers} className="text-blue-500 mr-2 mt-1" />
                                <span><strong>Ver Contactos:</strong> Acceder al listado de contactos</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faEnvelope} className="text-purple-500 mr-2 mt-1" />
                                <span><strong>Ver Campañas:</strong> Ver todas las campañas</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Crear Campaña */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faPlus} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Crear una Campaña
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 1: Información Básica
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Completa los campos básicos de la campaña:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Marca:</strong> Selecciona la marca para la campaña</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Nombre de la Campaña:</strong> Nombre descriptivo (ej: "Newsletter Diciembre 2025")</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Asunto del Email:</strong> Asunto que verán los destinatarios en su bandeja de entrada</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-2 mt-1" />
                                <span><strong>Contenido Texto Plano (Opcional):</strong> Versión en texto plano del email</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-2 mt-1" />
                                <span><strong>Fecha Programada (Opcional):</strong> Programa el envío para una fecha y hora específica</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 2: Crear el Contenido del Email
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Usa el <strong>Editor WYSIWYG</strong> (What You See Is What You Get) para crear el contenido 
                            del email de forma visual. El editor incluye:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Formato de Texto</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Negrita, cursiva, subrayado, tachado</li>
                                    <li>• Encabezados (H1, H2, H3)</li>
                                    <li>• Colores de texto y fondo</li>
                                </ul>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Estructura</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Listas ordenadas y con viñetas</li>
                                    <li>• Alineación (izquierda, centro, derecha)</li>
                                    <li>• Enlaces e imágenes</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 3: Insertar Placeholders
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Los placeholders son variables dinámicas que se reemplazan con información personalizada 
                            para cada destinatario. Usa los botones de la barra de placeholders para insertar:
                        </p>
                        <div className="space-y-3">
                            <div className="border-l-4 border-blue-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Placeholders de Contacto</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{contact.name}}"}</code> - Nombre completo</li>
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{contact.email}}"}</code> - Email</li>
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{contact.firstName}}"}</code> - Primer nombre</li>
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{contact.lastName}}"}</code> - Apellido</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-green-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Placeholders de Marca</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{marca.name}}"}</code> - Nombre de la marca</li>
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{marca.email}}"}</code> - Email de contacto</li>
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{marca.phone}}"}</code> - Teléfono</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-red-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Enlace de Baja (Requerido)</h4>
                                <p className="text-sm text-gray-600 mb-2">
                                    <strong>⚠️ Importante:</strong> Todos los emails deben incluir un enlace de baja para cumplir con GDPR.
                                </p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{unsubscribe.url}}"}</code> - URL completa de baja</li>
                                    <li>• <code className="bg-gray-100 px-1 rounded">{"{{unsubscribe.link}}"}</code> - Enlace de baja</li>
                                </ul>
                                <div className="bg-red-50 border border-red-200 rounded p-3 mt-3">
                                    <p className="text-xs text-red-800">
                                        <strong>⚠️ Advertencia:</strong> Si no incluyes el enlace de baja, verás una advertencia 
                                        y no podrás enviar la campaña hasta agregarlo.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 4: Vista Previa
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Antes de enviar, puedes ver una vista previa del email:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Haz clic en el botón <strong>"Vista Previa"</strong> o <strong>"Preview"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Se abrirá un modal mostrando cómo se verá el email</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Revisa el contenido y asegúrate de que todo se vea correctamente</span>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* Segmentación */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faFilter} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Segmentación de Destinatarios
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Segmentos RFM
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes segmentar por los 6 segmentos RFM basados en análisis de comportamiento:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Campeones</h4>
                                <p className="text-sm text-gray-600">
                                    Clientes más valiosos y frecuentes
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Clientes Fieles</h4>
                                <p className="text-sm text-gray-600">
                                    Clientes regulares y leales
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">En Riesgo</h4>
                                <p className="text-sm text-gray-600">
                                    Clientes que están disminuyendo su actividad
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">No Pueden Perderse</h4>
                                <p className="text-sm text-gray-600">
                                    Clientes valiosos pero poco frecuentes
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Otros Filtros de Segmentación
                        </h3>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Segmentos de productos:</strong> Filtra por tipo de comprador (compradores frecuentes, ocasionales, etc.)</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Segmentos de comportamiento:</strong> Filtra por patrones de comportamiento</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tags personalizados:</strong> Filtra por etiquetas que hayas asignado a los contactos</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Filtros geográficos:</strong> País y ciudad</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Filtro de actividad:</strong> Días desde última actividad</span>
                            </li>
                        </ul>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>💡 Contador en Tiempo Real:</strong> Mientras configuras los filtros, verás 
                                automáticamente cuántos destinatarios cumplen los criterios seleccionados.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Enviar Campaña */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faPaperPlane} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Enviar una Campaña
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Guardar como Borrador
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes guardar la campaña como borrador para enviarla más tarde:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Completa la información básica y el contenido del email</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Haz clic en <strong>"Guardar"</strong> o <strong>"Guardar Borrador"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>La campaña se guardará con estado "Borrador" y podrás editarla o enviarla más tarde</span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Enviar Campaña
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Para enviar una campaña:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Desde el listado de campañas, encuentra la campaña que quieres enviar</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Haz clic en el botón <strong>"Enviar"</strong> (icono de avión)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Confirma el envío. El sistema comenzará a enviar los emails automáticamente</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Puedes ver el progreso en tiempo real desde la página de progreso</span>
                            </li>
                        </ol>
                        <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
                            <p className="text-sm text-green-800">
                                <strong>✅ Control Automático:</strong> El sistema controla automáticamente la velocidad de envío 
                                (60 emails/minuto, 1,000/hora, 10,000/día) para evitar problemas con los servidores de email.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Estadísticas */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faChartBar} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Ver Estadísticas
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Métricas Disponibles
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Para cada campaña puedes ver estadísticas detalladas:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Envío</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Total destinatarios</li>
                                    <li>• Emails enviados</li>
                                    <li>• Emails fallidos</li>
                                    <li>• Emails pendientes</li>
                                </ul>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Resultados</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Bajas (unsubscribes)</li>
                                    <li>• Bounces (rebotes)</li>
                                    <li>• Tasas de envío y entrega</li>
                                </ul>
                            </div>
                        </div>
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mt-4">
                            <p className="text-sm text-purple-800">
                                <strong>📊 Gráficos:</strong> Las estadísticas incluyen gráficos visuales (pie charts y bar charts) 
                                para ayudarte a entender mejor el rendimiento de tus campañas.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Gestión de Contactos */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faUsers} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Gestión de Contactos
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Listado de Contactos
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes gestionar tus contactos desde la sección de contactos:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Ver contactos:</strong> Lista todos los contactos con paginación</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Buscar:</strong> Busca contactos por email o nombre</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Filtrar:</strong> Filtra por marca, tags o consentimiento</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Consentimiento GDPR:</strong> Gestiona el consentimiento de marketing de cada contacto</span>
                            </li>
                        </ul>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>🔒 Importante:</strong> Solo los contactos con consentimiento activo recibirán emails. 
                                Esto es obligatorio para cumplir con GDPR.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Consejos Finales */}
                <div className="bg-purple-50 rounded-lg p-8 mb-8">
                    <h3 className="text-lg font-semibold text-purple-800 mb-4">
                        💡 Consejos para Email Marketing Exitoso
                    </h3>
                    <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Personaliza tus emails:</strong> Usa placeholders para hacer que cada email se sienta personalizado</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Segmenta inteligentemente:</strong> Usa la segmentación RFM para enviar mensajes relevantes a cada grupo</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Incluye siempre el enlace de baja:</strong> Es obligatorio y mejora la confianza de tus clientes</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Revisa las estadísticas:</strong> Analiza qué campañas funcionan mejor para optimizar futuras campañas</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Prueba antes de enviar:</strong> Usa la vista previa para asegurarte de que todo se ve correctamente</span>
                        </li>
                    </ul>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-6 border-t">
                    <Link
                        to="/documentation/marketing/recompensas"
                        className="text-gray-600 hover:text-gray-800 font-medium inline-flex items-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Recompensas
                    </Link>
                    <Link
                        to="/documentation/marketing/infografias"
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center"
                    >
                        Siguiente: Infografías
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default MarketingEmail;

